// Set on <body> while the on-screen keyboard is open.
export const KEYBOARD_OPEN_CLASS = "hw-keyboard-open";

// Smaller height changes are browser toolbars showing or hiding, not a keyboard.
const MIN_KEYBOARD_PX = 150;

// How long a focus change waits before it is acted on, unless a resize comes first.
const FOCUS_SETTLE_MS = 300;

const NON_TEXT_INPUT_TYPES = new Set([
  "button", "checkbox", "color", "date", "datetime-local", "file", "hidden", "image",
  "month", "radio", "range", "reset", "submit", "time", "week",
]);

export const isTextEntry = (el) =>
  !!el &&
  (el.isContentEditable ||
    (el.tagName === "TEXTAREA" && !el.readOnly) ||
    (el.tagName === "INPUT" && !el.readOnly && !NON_TEXT_INPUT_TYPES.has(el.type)));

// Toasts are fixed to the bottom of the layout viewport. The Android WebView shrinks
// that viewport for the keyboard, but iOS doesn't (the app has no keyboard plugin), so
// there a toast answering a search sent with the keyboard's Go key is drawn under the
// keyboard, and Go seems to do nothing. Call this before such a toast: if the keyboard
// is open, it closes it by taking focus from the field (the text stays), and the toast
// then shows above the tab bar on both. With no soft keyboard open (a desktop, or a
// hardware keyboard) the field keeps focus. Returns whether it closed the keyboard.
export const closeSoftKeyboard = (doc = document) => {
  const el = doc.activeElement;
  if (!doc.body.classList.contains(KEYBOARD_OPEN_CLASS) || !isTextEntry(el)) return false;
  el.blur();
  return true;
};

// The Android WebView shrinks the viewport when the keyboard opens, so whatever is fixed
// to the bottom of the screen (the tab bar, raised toasts) rides on top of the keyboard
// and covers what little room is left. The web gets no keyboard events, so infer it: a
// text field has focus and the viewport is well short of its height without the keyboard.
// Returns a cleanup function.
export const watchSoftKeyboard = (win = window) => {
  const doc = win.document;
  const vv = win.visualViewport;
  // The viewport's height without the keyboard, for each width it has had: rotating with
  // the keyboard up and back again mustn't leave the shortened height as the reference.
  const fullHeights = new Map();
  let focusTimer;

  const update = (resized) => {
    // Scaled back up so that pinch-zooming doesn't read as a keyboard.
    const width = vv ? Math.round(vv.width * vv.scale) : win.innerWidth;
    const height = vv ? vv.height * vv.scale : win.innerHeight;
    const typing = isTextEntry(doc.activeElement);
    // With no text field focused there is no keyboard, so a resize then gives the full
    // height. A focus change alone doesn't: the keyboard may still be on its way down.
    const known = fullHeights.get(width);
    if (known === undefined || height > known || (resized && !typing)) fullHeights.set(width, height);
    doc.body.classList.toggle(
      KEYBOARD_OPEN_CLASS,
      typing && height < fullHeights.get(width) - MIN_KEYBOARD_PX
    );
  };

  const onResize = () => update(true);
  // Deferred: a tap that takes focus from the field does so on its mousedown, and the tab
  // bar showing again right then would be under the finger by the mouseup, so the click
  // would go to the tab bar. The keyboard closing resizes the viewport, which updates sooner.
  const onFocusChange = () => {
    clearTimeout(focusTimer);
    focusTimer = setTimeout(() => update(false), FOCUS_SETTLE_MS);
  };
  const resizeTarget = vv || win;

  update(false);
  resizeTarget.addEventListener("resize", onResize);
  doc.addEventListener("focusin", onFocusChange);
  doc.addEventListener("focusout", onFocusChange);

  return () => {
    clearTimeout(focusTimer);
    resizeTarget.removeEventListener("resize", onResize);
    doc.removeEventListener("focusin", onFocusChange);
    doc.removeEventListener("focusout", onFocusChange);
    doc.body.classList.remove(KEYBOARD_OPEN_CLASS);
  };
};

// Android's Back key puts the keyboard away but leaves the field focused, so no blur tells
// the field's own popups (an autocomplete list) to close. Calls back with the field when
// the keyboard goes down and a text field still has focus. Returns a cleanup function.
export const onSoftKeyboardHidden = (callback, doc = document) => {
  let open = doc.body.classList.contains(KEYBOARD_OPEN_CLASS);
  const observer = new MutationObserver(() => {
    const wasOpen = open;
    open = doc.body.classList.contains(KEYBOARD_OPEN_CLASS);
    if (wasOpen && !open && isTextEntry(doc.activeElement)) callback(doc.activeElement);
  });
  observer.observe(doc.body, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
};
